import { createModel } from "hox";
import { useState, useEffect, useRef } from "react";
import { IMaintenanceDetail, IMaintenanceSearch } from "../maintenanceType";
import { DEFAULT_FIRST_PAGE_NUM, DEFAULT_PAGE_SIZE, } from "@/utils/constants";
import { TablePaginationConfig } from "antd/es/table/interface";
import { transPgToPagination, repairFormatNum } from "@/utils/utils";
import { ISearchPage } from "@/types/ScmTypes";
import { maintenanceService } from "../maintenanceService";
import { message } from "antd";
import useGlobalModel from "@/model/globalModel";
import { sourceAppOrBranch } from "@/types/VrmSaDictEnums";
import { convertRes2Blob } from "@/utils/exportFile";
import { useLockFn } from "ahooks";

// 计算滞留时间（xx天xx时）
const calculateKeepTime = (createTime: string): string => {
    if (!createTime) return "--";

    const createDate = new Date(createTime).getTime();
    const now = new Date().getTime();
    const durationMs = now - createDate;

    if (durationMs < 0) return "0时";

    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;

    const parts = [];
    if (days > 0) parts.push(`${days}天`);
    parts.push(`${remainingHours}时`);

    return parts.join('');
};

// 计算到下一个整点的毫秒数
const getMsToNextHour = (): number => {
    const now = new Date();
    const nextHour = new Date(now);
    nextHour.setHours(now.getHours() + 1);
    nextHour.setMinutes(0);
    nextHour.setSeconds(0);
    nextHour.setMilliseconds(0);
    return nextHour.getTime() - now.getTime();
};

export interface IDescTotal {
    discountTotalAmount: number, // 321.94
    receivableAmount: number, // 2390.3
    receivableTotalAmount: number, // 7970.01
    totalAmount: number, // 8291.94
    workHour: number,
    materialNum: number
}

export interface IPrint {
    visible: boolean,
    type: number,
}

export const useMaintenanceModel = createModel(function () {
    const { shopAndOrg, setLoadingInfo } = useGlobalModel(({ shopAndOrg, setLoadingInfo }) =>
        [shopAndOrg, setLoadingInfo]);
    const [print, setPrint] = useState<boolean>(false);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    const initDesc = {
        discountTotalAmount: 0,
        receivableAmount: 0,
        receivableTotalAmount: 0,
        totalAmount: 0,
        workHour: 0,
        materialNum: 0
    }
    const allDesc = {
        allInNum: 0,
        allFinishNum: 0,
        allWaitNum: 0,
    }
    // 组件初始化和店铺切换时重置数据并设置定时刷新
    useEffect(() => {
        reset();

        // 设置定时器，每小时刷新一次数据
        const setupTimer = () => {
            // 清除现有定时器
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }

            // 计算到下一个整点的时间
            const msToNextHour = getMsToNextHour();

            // 设置定时器
            timerRef.current = setTimeout(() => {
                // 到点后刷新数据
                loadData();
                // 继续设置下一个定时器
                setupTimer();
            }, msToNextHour);
        };

        setupTimer();

        // 组件卸载时清除定时器
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [shopAndOrg.shopCode])

    const initSearchParam: IMaintenanceSearch = {};
    const initDataSource: IMaintenanceDetail[] = [];
    const initPgCfg: TablePaginationConfig = {
        pageSize: DEFAULT_PAGE_SIZE,
        current: DEFAULT_FIRST_PAGE_NUM
    };

    // 查询条件
    const [searchParam, setSearchParam] = useState<IMaintenanceSearch>(initSearchParam);
    const [printParam, setPrintParam] = useState<IMaintenanceSearch>(initSearchParam);

    // 列表数据源
    const [dataSource, setDataSource] = useState<IMaintenanceDetail[]>(initDataSource);

    // 表格选中行Key值
    const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

    // 表格选中的值
    const [selectedRow, setSelectedRow] = useState<IMaintenanceDetail>({});
    const [descTotal, setDescTotal] = useState<IDescTotal>(initDesc);
    const [allTotal, setAllTotal] = useState<any>({});
    // 选中的数据统计展示的数据
    const [rowsDescTotal, setRowsDescTotal] = useState<IDescTotal>(initDesc);
    const [descRows, setDescRows] = useState<IMaintenanceDetail[]>([]);

    // 列表数据源分页参数
    const [pgCfg, setPgCfg] = useState<TablePaginationConfig>(initPgCfg);
    const [loading, setLoading] = useState<boolean>(false);

    // 结算单打印
    const [printSettleVisible, setPrintSettleVisible] = useState<boolean>(false);

    useEffect(() => {
        let filters = dataSource.filter(el =>
            selectedRowKeys.length > 0 ? el.id === selectedRowKeys[0] : false);
        const selectedData = filters && filters.length > 0 ? filters[0] : undefined;
        setSelectedRow(selectedData as IMaintenanceDetail)
    }, [selectedRowKeys, dataSource])

    useEffect(() => {
        let newRowsDescTotal = { ...initDesc }
        descRows.forEach((item) => {
            let { discountTotalAmount, receivableTotalAmount, receivableAmount, totalAmount, materialNum, workHour } = item
            newRowsDescTotal.discountTotalAmount += +repairFormatNum(discountTotalAmount || 0)
            newRowsDescTotal.receivableAmount += +repairFormatNum(receivableAmount || 0)
            newRowsDescTotal.receivableTotalAmount += +repairFormatNum(receivableTotalAmount || 0)
            newRowsDescTotal.totalAmount += +repairFormatNum(totalAmount || 0)
            newRowsDescTotal.workHour += +repairFormatNum(workHour || 0)
            newRowsDescTotal.materialNum += +repairFormatNum(materialNum || 0)
        })
        setRowsDescTotal(newRowsDescTotal)
    }, [descRows])

    const updateSearchParam = (updateParam: IMaintenanceSearch) => {
        setSearchParam({ ...searchParam, ...updateParam })
    }

    const resetSearchParam = () => {
        setSearchParam(initSearchParam);
        loadData(initPgCfg, {});
        setSelectedRowKeys([])
    }

    const reset = () => {
        setSearchParam(initSearchParam);
        setSelectedRowKeys([])
    }

    const loadData = async (page?: ISearchPage, searchValue: IMaintenanceSearch = searchParam) => {
        setLoading(true)
        const shopParam = {
            shopType: searchValue.shopId !== undefined ? 1 : shopAndOrg.shopType,
            shopId: searchValue.shopId ?? shopAndOrg.shopId
        };

        const searchParams = {
            status: "maintenance.approved,maintenance.under-review,maintenance.return-visit,maintenance.write-off,maintenance.underway,maintenance.await,maintenance.trans,maintenance.stock-io,maintenance.create",
            ...searchValue,
            ...shopParam,
            ...sourceAppOrBranch,
            brandCodes: searchValue.brandCode ? searchValue.brandCode.toString() : undefined,
            pageSize: page?.pageSize ?? pgCfg.pageSize ?? DEFAULT_PAGE_SIZE,
            pageNum: (page?.pageNum || page?.current) ?? pgCfg.current ?? DEFAULT_FIRST_PAGE_NUM,
        }

        setPrintParam(searchParams);

        try {
            let { retData } = await maintenanceService.page(searchParams);
            const { records, ...pg } = retData;

            // 为每条记录计算滞留时间
            const dataWithKeepTime = records.map(record => ({
                ...record,
                keepTime: calculateKeepTime(record.createTime)
            }));

            setPgCfg(transPgToPagination(pg));
            setDataSource(dataWithKeepTime);
            // loadTotal({
            //     ...shopParam,
            //     timeType: "all"
            // });
        } catch (error) {
            console.error("加载数据失败:", error);
            message.error("加载数据失败");
        } finally {
            setLoading(false);
        }
    }

    const loadTotal = async (searchParams: any) => {
        try {
            let { retData: statistics } = await maintenanceService.pointAll(searchParams);
            setAllTotal(statistics || allDesc);
        } catch (error) {
            console.error("加载统计数据失败:", error);
        }
    }

    const invalidateData = async (obj: object) => {
        try {
            let { retData } = await maintenanceService.invalidate(obj)
            if (retData) {
                message.success("作废成功");
                loadData();
            }
        } catch (error) {
            console.error("作废失败:", error);
            message.error("作废失败");
        }
    }

    const tran = useLockFn(async (obj: object) => {
        try {
            let { retData } = await maintenanceService.tran(obj)
            loadData();
            return retData;
        } catch (error) {
            console.error("转换失败:", error);
            message.error("转换失败");
            return null;
        }
    })

    const exportMaintenance = async (searchValue: IMaintenanceSearch = searchParam) => {
        setLoadingInfo({ loadingVisible: true, txt: "快速导出中" });
        const searchParams = {
            status: "maintenance.approved,maintenance.under-review,maintenance.return-visit,maintenance.write-off,maintenance.underway,maintenance.await,maintenance.trans,maintenance.stock-io,maintenance.create",
            ...searchValue,
            ...shopAndOrg,
            brandCodes: searchValue.brandCode ? searchValue.brandCode.toString() : undefined,
            id: selectedRowKeys.join(),
        }

        try {
            let result = await maintenanceService.export(searchParams);
            result && setLoadingInfo({ loadingVisible: false, txt: "" });
            convertRes2Blob(result);
        } catch (error) {
            console.error("导出失败:", error);
            message.error("导出失败");
            setLoadingInfo({ loadingVisible: false, txt: "" });
        }
    }

    const [scaniaPrint, setScaniaPrint] = useState<IPrint>({ visible: false, type: 1 });
    const [settleScaniaPrint, setSettleScaniaPrint] = useState<boolean>(false);

    const detailsExport = async (searchValue: IMaintenanceSearch = searchParam) => {
        setLoadingInfo({ loadingVisible: true, txt: "快速导出中" });
        const searchParams = {
            status: "maintenance.approved,maintenance.under-review,maintenance.return-visit,maintenance.write-off,maintenance.underway,maintenance.await,maintenance.trans,maintenance.stock-io,maintenance.create",
            ...searchValue,
            ...shopAndOrg,
            brandCodes: searchValue.brandCode ? searchValue.brandCode.toString() : undefined,
            id: selectedRowKeys.join(),
        }

        try {
            let result = await maintenanceService.detailsExport(searchParams);
            result && setLoadingInfo({ loadingVisible: false, txt: "" });
            convertRes2Blob(result);
        } catch (error) {
            console.error("详情导出失败:", error);
            message.error("详情导出失败");
            setLoadingInfo({ loadingVisible: false, txt: "" });
        }
    }

    const detailsNoPriceExport = async (searchValue: IMaintenanceSearch = searchParam) => {
        setLoadingInfo({ loadingVisible: true, txt: "快速导出中" });
        const searchParams = {
            status: "maintenance.approved,maintenance.under-review,maintenance.return-visit,maintenance.write-off,maintenance.underway,maintenance.await,maintenance.trans,maintenance.stock-io,maintenance.create",
            ...searchValue,
            ...shopAndOrg,
            brandCodes: searchValue.brandCode ? searchValue.brandCode.toString() : undefined,
            id: selectedRowKeys.join(),
        }

        try {
            let result = await maintenanceService.detailsNoPriceExport(searchParams);
            result && setLoadingInfo({ loadingVisible: false, txt: "" });
            convertRes2Blob(result);
        } catch (error) {
            console.error("无价格详情导出失败:", error);
            message.error("无价格详情导出失败");
            setLoadingInfo({ loadingVisible: false, txt: "" });
        }
    }

    return {
        searchParam, loading,
        dataSource,
        selectedRowKeys,
        selectedRow,
        pgCfg,
        initPgCfg,
        setSearchParam,
        setDataSource,
        setSelectedRowKeys,
        setSelectedRow,
        setPgCfg,
        updateSearchParam,
        resetSearchParam,
        loadData,
        invalidateData,
        tran,
        print, setPrint,
        exportMaintenance,
        descTotal, setDescTotal,
        scaniaPrint, setScaniaPrint,
        settleScaniaPrint, setSettleScaniaPrint,
        detailsExport,
        rowsDescTotal, setRowsDescTotal,
        descRows, setDescRows,
        reset,
        printSettleVisible, setPrintSettleVisible,
        printParam, setPrintParam,
        detailsNoPriceExport,
        allTotal, setAllTotal
    }
})
